import React from 'react';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import theme from './theme';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ContentProvider } from './contexts/ContentContext';
import { WatchlistProvider } from './contexts/WatchlistContext';
import { SearchProvider } from './contexts/SearchContext';
import { UnifiedContentProvider } from './contexts/UnifiedContentContext';
import { VideoPlayerProvider } from './contexts/VideoPlayerContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { CookieConsent } from './components/CookieConsent';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Search from './pages/Search';
import EnhancedSearch from './pages/EnhancedSearch';
import SearchAnalytics from './pages/SearchAnalytics';
import ContentManagement from './pages/ContentManagement';
import Analytics from './pages/Analytics';
import SubscriptionManagement from './pages/SubscriptionManagement';
import Movies from './pages/Movies';
import Series from './pages/Series';
import Shorts from './pages/Shorts';
import Anime from './pages/Anime';
import Books from './pages/Books';
import Comics from './pages/Comics';
import ComicVolumes from './pages/ComicVolumes';
import ComicReader from './pages/ComicReader';
import Novels from './pages/Novels';
import ContentDetail from './pages/ContentDetail';
import CreatorProfile from './pages/CreatorProfile';
import CreatorDashboard from './pages/CreatorDashboard';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Upload from './pages/Upload';
import UploadCategorySelection from './pages/UploadCategorySelection';
import UploadMovie from './pages/UploadMovie';
import UploadShort from './pages/UploadShort';
import UploadComic from './pages/UploadComic';
import UploadBook from './pages/UploadBook';
import UploadSeries from './pages/UploadSeries';
import UploadSeriesMultiStep from './pages/UploadSeriesMultiStep';
import UploadVideo from './pages/UploadVideo';
import SeriesManagement from './pages/SeriesManagement';
import UploadEpisodeNew from './pages/UploadEpisodeNew';
import SeasonsManagement from './pages/SeasonsManagement';
import EpisodesManagement from './pages/EpisodesManagement';
import CreateSeason from './pages/CreateSeason';
import SeasonManagement from './pages/SeasonManagement';
import EpisodeDetail from './pages/EpisodeDetail';
import EditEpisode from './pages/EditEpisode';
import BookDetail from './pages/BookDetail';
import ComicDetail from './pages/ComicDetail';
import EditBook from './pages/EditBook';
import EditComic from './pages/EditComic';
import EditShorts from './pages/EditShorts';
import EditSeries from './pages/EditSeries';
import EditMovie from './pages/EditMovie';
import CreatorAnalytics from './pages/CreatorAnalytics';
import VideoPlayer from './pages/VideoPlayer';
import Watchlist from './pages/Watchlist';
import AddEpisode from './pages/AddEpisode';
import AddChapter from './pages/AddChapter';
import VideoPlayerRoute from './pages/VideoPlayerRoute';
import ShortPlayer from './pages/ShortPlayer';
import TestAuth from './pages/TestAuth';
import AdminModeration from './pages/AdminModeration';
import EmailVerificationTest from './pages/EmailVerificationTest';
import EmailVerification from './pages/EmailVerification';
import Donation from './pages/Donation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataExport from './pages/DataExport';

// Debug utilities for development
if (process.env.NODE_ENV === 'development') {
  import('./utils/debugOverflow').then(module => {
    // Make debug functions available globally in development
    (window as any).debugOverflow = module;
  });
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ChakraProvider theme={theme}>
        <ColorModeScript initialColorMode={theme.config.initialColorMode} />
        <ThemeProvider>
          <AuthProvider>
            <ContentProvider>
              <WatchlistProvider>
                <SearchProvider>
                  <UnifiedContentProvider>
                    <VideoPlayerProvider>
                      <BrowserRouter>
                        <Layout>
                          <CookieConsent />
                          <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/search" element={<Search />} />
                          <Route path="/search-enhanced" element={<EnhancedSearch />} />
                          <Route path="/search-analytics" element={<SearchAnalytics />} />
                          <Route path="/content-management" element={<ContentManagement />} />
                          <Route path="/movies" element={<Movies />} />
                          <Route path="/series" element={<Series />} />
                          <Route path="/shorts" element={<Shorts />} />
                          <Route path="/comics" element={<Comics />} />
                          <Route path="/novels" element={<Novels />} />
                          <Route path="/anime" element={<Anime />} />
                          <Route path="/books" element={<Books />} />
                          <Route path="/content/:id" element={<ContentDetail />} />
                          <Route path="/book/:id" element={<BookDetail />} />
                          <Route path="/book/:id/edit" element={<EditBook />} />
                          <Route path="/comic/:id/edit" element={<EditComic />} />
                          <Route path="/series/:id/edit" element={<EditSeries />} />
                          <Route path="/movie/:id/edit" element={<EditMovie />} />
                          <Route path="/comic/:id" element={<ComicDetail />} />
                          <Route path="/comic/:id/volumes" element={<ComicVolumes />} />
                          <Route path="/comic/:id/read" element={<ComicReader />} />
                          <Route path="/creator/:id" element={<CreatorProfile />} />
                          <Route path="/creator-dashboard" element={<CreatorDashboard />} />
                          <Route path="/creator-analytics" element={<CreatorAnalytics />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/profile/:id" element={<CreatorProfile />} />
                          <Route path="/edit-profile" element={<EditProfile />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path="/upload" element={<Upload />} />
                          <Route path="/upload/category" element={<UploadCategorySelection />} />
                          <Route path="/upload/movie" element={<UploadMovie />} />
                          <Route path="/upload/short" element={<UploadShort />} />
                          <Route path="/upload/comic" element={<UploadComic />} />
                          <Route path="/upload/book" element={<UploadBook />} />
                          <Route path="/upload/series" element={<UploadSeriesMultiStep />} />
                          <Route path="/upload/video" element={<UploadVideo />} />
                          <Route path="/series/:seriesId/season/:seasonId/upload-episode" element={<UploadEpisodeNew />} />
                          <Route path="/series/:seriesId/manage" element={<SeasonsManagement />} />
                          <Route path="/series/:seriesId/episodes" element={<SeriesManagement />} />
                          <Route path="/series/:seriesId/create-season" element={<CreateSeason />} />
                          <Route path="/series/:seriesId/season/:seasonId/manage" element={<SeasonManagement />} />
                          <Route path="/series/:seriesId/season/:seasonId/episodes" element={<EpisodesManagement />} />
                          <Route path="/episode/:episodeId" element={<EpisodeDetail />} />
                          <Route path="/episode/:episodeId/edit" element={<EditEpisode />} />
                          <Route path="/add-episode" element={<AddEpisode />} />
                          <Route path="/add-chapter" element={<AddChapter />} />
                          <Route path="/player" element={<VideoPlayerRoute />} />
                          <Route path="/player/:id" element={<VideoPlayerRoute />} />
                          <Route path="/video/:id" element={<VideoPlayer />} />
                          <Route path="/short/:id" element={<ShortPlayer />} />
                          <Route path="/short/:id/edit" element={<EditShorts />} />
                          <Route path="/watchlist" element={<Watchlist />} />
                          <Route path="/admin-moderation" element={<AdminModeration />} />
                          <Route path="/test-auth" element={<TestAuth />} />
                          <Route path="/email-test" element={<EmailVerificationTest />} />
                          <Route path="/verify-email" element={<EmailVerification />} />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route path="/subscription-management" element={<SubscriptionManagement />} />
                            <Route path="/donation/:creatorId/:contentId?" element={<Donation />} />
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<TermsOfService />} />
                            <Route path="/data-export" element={<DataExport />} />
                          </Routes>
                        </Layout>
                      </BrowserRouter>
                    </VideoPlayerProvider>
                  </UnifiedContentProvider>
                </SearchProvider>
              </WatchlistProvider>
            </ContentProvider>
          </AuthProvider>
        </ThemeProvider>
      </ChakraProvider>
    </ErrorBoundary>
  );
};

export default App;